/* Memoised visibility verdicts, cleared once per detection run. Two independent
 * caches live here; the viewport bounds live in viewport.ts. */

type VisibilityWeakCache = WeakMap<HTMLElement, boolean>;
export type QuickCheckOptions = { minHeight: number; minWidth: number };

const setAll = (map: VisibilityWeakCache, els: HTMLElement[], visible: boolean) => els.forEach((el) => map.set(el, visible));

/** `isVisible` keeps two verdict tables: `noOpacity` answers the `opacity:false`
 * question ("visible ignoring opacity") and `withOpacity` the `opacity:true` question
 * ("visible considering opacity"). They share work across modes within a run. */
class VisibilityCache {
  private noOpacity: VisibilityWeakCache = new WeakMap();
  private withOpacity: VisibilityWeakCache = new WeakMap();

  read(el: HTMLElement, considerOpacity: boolean): boolean | undefined {
    return considerOpacity ? (this.withOpacity.get(el) ?? this.noOpacity.get(el)) : this.noOpacity.get(el);
  }

  writeChain(chain: HTMLElement[], visible: boolean, considerOpacity: boolean, hiddenByOpacity: boolean): void {
    if (!considerOpacity) return setAll(this.noOpacity, chain, visible);
    /* The opacity-independent part of the verdict also seeds `noOpacity`: a visible
     * result, or a hidden-for-a-non-opacity-reason result. When hidden purely by
     * opacity, the transparent node (always last) is dropped via `slice`. */
    if (visible || !hiddenByOpacity) setAll(this.noOpacity, chain, visible);
    else setAll(this.noOpacity, chain.slice(0, -1), visible);
    setAll(this.withOpacity, chain, visible);
  }

  clear(): void {
    this.noOpacity = new WeakMap();
    this.withOpacity = new WeakMap();
  }
}

/** Leaf quick-check verdicts, bucketed by the `(minWidth,minHeight)` threshold used. */
class QuickCache {
  private readonly buckets = new Map<string, VisibilityWeakCache>();

  private bucket(opts: QuickCheckOptions): VisibilityWeakCache {
    const key = `${opts.minWidth}:${opts.minHeight}`;
    let map = this.buckets.get(key);
    if (!map) this.buckets.set(key, (map = new WeakMap()));
    return map;
  }

  get(el: HTMLElement, opts: QuickCheckOptions): boolean | undefined {
    return this.bucket(opts).get(el);
  }

  set(el: HTMLElement, opts: QuickCheckOptions, visible: boolean): void {
    this.bucket(opts).set(el, visible);
  }

  clear(): void {
    this.buckets.clear();
  }
}

/** Detection-run-scoped singletons. Reset together by `clearVisibilityCache`. */
export const visibilityCache = new VisibilityCache();
export const quickCache = new QuickCache();
